import * as React from 'react'
import { RefreshCw, TriangleAlert, X } from 'lucide-react'
import { IconButton } from '@/components/ui/icon-button'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useT } from '@/components/visitone/visitone-provider'
import type { ModuleRefusal } from '@/components/today/modules/types'

/*
 * The frame every Today module renders inside. It is a labelled `section` whose
 * accessible name matches its visible `h2` heading (VIS-257 a11y), carries an
 * optional dismiss control for dismissible modules, and renders its own loading
 * skeleton and inline error/retry so a single module failing never blocks the
 * others. The module heading is always an `h2`; figures inside are never
 * headings. A `refusal` (the BFF refused a change the module offered,
 * VIS-329) is an alert at the top of the module that takes focus.
 */
export function ModuleShell({
  title,
  headingId,
  isLoading,
  error,
  onRetry,
  onDismiss,
  refusal,
  loadingRows = 3,
  children,
}: {
  title: string
  headingId: string
  isLoading: boolean
  error: unknown
  onRetry: () => void
  onDismiss?: () => void
  refusal?: ModuleRefusal
  loadingRows?: number
  children: React.ReactNode
}) {
  const t = useT()
  const refusalRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    if (refusal) refusalRef.current?.focus()
  }, [refusal])

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-4 rounded-md border border-line bg-surface p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 id={headingId} className="text-base text-fg-strong" style={{ fontWeight: 650 }}>
          {title}
        </h2>
        {onDismiss && !isLoading ? (
          <IconButton
            label={t('today.module.dismiss', { module: title })}
            onClick={onDismiss}
            className="-mr-2 -mt-1"
          >
            <X />
          </IconButton>
        ) : null}
      </div>

      {refusal ? (
        <div
          ref={refusalRef}
          tabIndex={-1}
          role="alert"
          className="rounded-sm border border-danger/60 bg-danger-surface px-4 py-3 outline-none"
        >
          <p className="text-sm font-semibold text-danger">{refusal.title}</p>
          <p className="mt-1 text-sm text-fg">{refusal.body}</p>
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex flex-col gap-3" aria-hidden="true">
          {Array.from({ length: loadingRows }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : error ? (
        <div
          role="alert"
          className="flex flex-col items-start gap-2 rounded-sm border border-danger/40 bg-danger-surface p-4"
        >
          <p className="inline-flex items-center gap-2 text-sm font-medium text-danger">
            <TriangleAlert aria-hidden="true" className="size-4" />
            {t('today.module.errorTitle')}
          </p>
          <p className="text-sm text-fg-muted">{t('today.module.errorBody')}</p>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            <RefreshCw />
            {t('today.module.retry')}
          </Button>
        </div>
      ) : (
        children
      )}
    </section>
  )
}
